import React, { useState } from 'react';
import {
    Box,
    IconButton,
    Popover,
    TextField,
    TableSortLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Badge,
    Tooltip
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useAsyncDebounce } from 'react-table';
import { ClearAll } from '@mui/icons-material';
import SimpleDatePicker from '../ui/simpleDatePicker';

const NUMERIC_OPERATORS = [
    { value: 'eq', label: 'Equal to' },
    { value: 'lt', label: 'Less than' },
    { value: 'lte', label: 'Less than or equal' },
    { value: 'gt', label: 'Greater than' },
    { value: 'gte', label: 'Greater than or equal' },
    { value: 'between', label: 'Between' }
];

const DATE_OPERATORS = [
    { value: 'eq', label: 'Equal to' },
    { value: 'lt', label: 'Before' },
    { value: 'lte', label: 'Before or on' },
    { value: 'gt', label: 'After' },
    { value: 'gte', label: 'After or on' },
    { value: 'between', label: 'Between' }
];

const NumericFilter = ({ value = { op: 'eq', value: '', value2: '' }, onChange }) => {
    // Local state to track values before sending to parent
    const [localValue, setLocalValue] = useState(value);

    const handleChange = (field, newValue) => {
        const updatedValue = { ...localValue, [field]: newValue };
        setLocalValue(updatedValue);

        // Only notify parent if we have both operator and value(s)
        const isComplete = updatedValue.op &&
            updatedValue.value &&
            (updatedValue.op !== 'between' || updatedValue.value2);

        if (isComplete) {
            onChange({ target: { value: updatedValue } });
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControl size="small" fullWidth>
                <InputLabel>Operator</InputLabel>
                <Select
                    value={localValue.op}
                    onChange={(e) => handleChange('op', e.target.value)}
                    label="Operator"
                >
                    {NUMERIC_OPERATORS.map((op) => (
                        <MenuItem key={op.value} value={op.value}>
                            {op.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <TextField
                size="small"
                label="Value"
                type="number"
                value={localValue.value}
                onChange={(e) => handleChange('value', e.target.value)}
                inputProps={{
                    min: 0,
                    step: "any"
                }}
            />
            {localValue.op === 'between' && (
                <TextField
                    size="small"
                    label="Second Value"
                    type="number"
                    value={localValue.value2}
                    onChange={(e) => handleChange('value2', e.target.value)}
                    inputProps={{
                        min: 0,
                        step: "any"
                    }}
                />
            )}
        </Box>
    );
};

const DateFilter = ({ value = { op: 'eq', value: '', value2: '' }, onChange }) => {
    const [localValue, setLocalValue] = useState(value);

    const handleChange = (field, newValue) => {
        const updatedValue = { ...localValue, [field]: newValue };
        setLocalValue(updatedValue);

        // Only notify parent if we have both operator and value(s)
        const isComplete = updatedValue.op &&
            updatedValue.value &&
            (updatedValue.op !== 'between' || updatedValue.value2);

        if (isComplete) {
            onChange({ target: { value: updatedValue } });
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControl size="small" fullWidth>
                <InputLabel>Operator</InputLabel>
                <Select
                    value={localValue.op}
                    onChange={(e) => handleChange('op', e.target.value)}
                    label="Operator"
                >
                    {DATE_OPERATORS.map((op) => (
                        <MenuItem key={op.value} value={op.value}>
                            {op.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <SimpleDatePicker
                label="Date"
                value={localValue.value}
                onChange={(value) => handleChange('value', value)}
            />
            {localValue.op === 'between' && (
                <SimpleDatePicker
                    label="End Date"
                    value={localValue.value2}
                    onChange={(value) => handleChange('value2', value)}
                />
            )}
        </Box>
    );
};

const FilterInput = ({ type, value, onChange, options }) => {
    switch (type) {
        case 'number':
            return (
                <NumericFilter value={value} onChange={onChange} />
            );
        case 'date':
            return <DateFilter value={value} onChange={onChange} />
        case 'enum':
            return (
                <FormControl size="small" fullWidth sx={{ minWidth: 200 }}>
                    <InputLabel>Filter</InputLabel>
                    <Select
                        value={value || ''}
                        onChange={(e) => {
                            // Add type information for enum filters
                            const newValue = {
                                type: 'enum',
                                value: e.target.value
                            };
                            onChange({ target: { value: newValue } });
                        }}
                        label="Filter"
                        autoFocus
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {options?.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        default:
            return (
                <TextField
                    autoFocus
                    size="small"
                    label="Filter"
                    value={value}
                    onChange={onChange}
                />
            );
    }
};

const ColumnHeaderWithFilter = ({ column }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    // Initialize with proper structure for numeric filters
    const [value, setValue] = useState(() => {
        if (column.type === 'number' || column.type === 'date') {
            return column.filterValue || { op: 'eq', value: '', value2: '' };
        }
        if (column.type === 'enum') {
            return column.filterValue || { type: 'enum', value: '' };
        }
        return column.filterValue || '';
    });
    const [previousValue, setPreviousValue] = useState(value);
    const open = Boolean(anchorEl);


    const handleClick = (event) => {
        event.stopPropagation();
        //setPreviousValue(value);
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (event) => {
        // Prevent event propagation to stop triggering sort
        if (event) {
            event.stopPropagation();
        }
        setAnchorEl(null);
        // If value was changed but popup was closed, reset to previous value
        // this is to prevent extra api call made to backend
        // if (value !== previousValue) {
        //     setValue(previousValue);
        //     column.setFilter(previousValue);
        // }
    };

    // Debounce the filter value changes
    const onChange = useAsyncDebounce(value => {
        if (open) { // Only update filter if popup is open
            column.setFilter(value);
        }
    }, 500);

    const handleFilterChange = (e) => {
        //const newValue = column.type === 'enum' ? e.target.value : e.target.value;
        const newValue = e.target.value
        setValue(newValue);
        onChange(newValue);
    };

    const handleReset = () => {
        const resetValue = column.type === 'number' ? { op: 'eq', value: '', value2: '' } : '';
        setValue(resetValue);
        column.setFilter(undefined);
        setAnchorEl(null);
    };

    // Helper to check if filter is active
    const hasActiveFilter = () => {
        if (!value) return false;

        if (column.type === 'number' || column.type === 'date') {
            // Check if operator and value(s) are set and not empty
            const hasValue = value.value !== '' && value.value !== null;
            const hasBothValuesForBetween = value.op === 'between' ?
                (value.value2 !== '' && value.value2 !== null) :
                true;

            return value.op && hasValue && hasBothValuesForBetween;
        }

        if (column.type === 'enum') {
            return value.value !== '';
        }

        return value !== '';
    };

    // Helper to get filter description
    const getFilterDescription = () => {
        if (!hasActiveFilter()) return '';

        if (column.type === 'enum') {
            const option = column.filterOptions?.find(opt => opt.value === value.value);
            return `Is: ${option?.label || value.value}`;
        }

        if (column.type === 'number') {
            const opLabel = NUMERIC_OPERATORS.find(op => op.value === value.op)?.label;
            if (value.op === 'between') {
                return `${opLabel} ${value.value} and ${value.value2}`;
            }
            return `${opLabel} ${value.value}`;
        }

        if (column.type === 'date') {
            const opLabel = DATE_OPERATORS.find(op => op.value === value.op)?.label;
            if (value.op === 'between') {
                return `${opLabel} ${value.value} and ${value.value2}`;
            }
            return `${opLabel} ${value.value}`;
        }

        return `Contains: ${value}`;
    };

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {column.render('Header')}
                    {column.canSort && (
                        <TableSortLabel
                            active={column.isSorted}
                            direction={column.isSortedDesc ? 'desc' : 'asc'}
                        />
                    )}
                </Box>
                {column.enableFilter && (
                    <>
                        <Tooltip title={hasActiveFilter() ? getFilterDescription() : 'Filter'}>
                            <Badge
                                variant="dot"
                                invisible={!hasActiveFilter()}
                                color="primary"
                            >
                                <IconButton
                                    size="small"
                                    onClick={handleClick}
                                    sx={{
                                        color: hasActiveFilter() ? 'primary.main' : 'inherit',
                                        opacity: hasActiveFilter() ? 1 : 0.5,
                                        '&:hover': { opacity: 1 }
                                    }}
                                >
                                    <FilterListIcon fontSize="small" />
                                </IconButton>
                            </Badge>
                        </Tooltip>
                        <Popover
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            marginThreshold={2}
                            // this to prevent click events from bubbling up
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Box sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FilterInput
                                        type={column.type}
                                        value={value}
                                        onChange={handleFilterChange}
                                        options={column.filterOptions}
                                    />
                                    {value && (
                                        <IconButton
                                            size="small"
                                            onClick={handleReset}
                                            sx={{ color: 'action.active' }}
                                        >
                                            <ClearAll fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>
                        </Popover>
                    </>
                )}
            </Box>
        </>

    );
};

export default ColumnHeaderWithFilter;